import React, { useState, useCallback, useEffect, useRef, use } from "react";
import { Form, Button, Card, Row, Col, InputGroup } from "react-bootstrap";
import { PlusCircle, Trash2 } from "lucide-react";
import { setFormValue, setStepIndex, setStepValid } from "../../../redux/globalJoyrideSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Create a new blank class form
const createNewClassTemplate = () => ({
  id: `temp_${Date.now()}_${Math.random()}`,
  class_name: "",
  numberOfSections: 1,
  selected_subjects: [],
  sectionTeachers: {},
});

const AddClassTab = ({
  subjects,
  teachers,
  loading,
  handleNewClassSubmit,
  runClassTour,
  classStepIndex,
  setActiveTab,
}) => {
  const [newClasses, setNewClasses] = useState([createNewClassTemplate()]);
  const scrollRef = useRef(null);
  const dispatch = useDispatch();
  const { run } = useSelector((state) => state.globalJoyride);
  const navigate = useNavigate();
  // Handle class field change
  const handleClassChange = useCallback((classId, field, value) => {
    setNewClasses((prevClasses) =>
      prevClasses.map((cls) =>
        cls.id === classId ? { ...cls, [field]: value } : cls
      )
    );
  }, []);

  // Teacher selection
  const handleTeacherChange = (classId, sectionIndex, teacherId) => {
    setNewClasses((prevClasses) =>
      prevClasses.map((cls) =>
        cls.id === classId
          ? {
            ...cls,
            sectionTeachers: {
              ...cls.sectionTeachers,
              [`section-${sectionIndex}`]: teacherId,
            },
          }
          : cls
      )
    );
  };

  // Subject selection
  const handleSubjectToggle = (classId, subject, isChecked) => {
    setNewClasses((prevClasses) =>
      prevClasses.map((cls) =>
        cls.id === classId
          ? {
            ...cls,
            selected_subjects: isChecked
              ? [
                ...cls.selected_subjects,
                {
                  subject_id: subject.subject_id,
                  subject_name: subject.subject_name,
                  max_periods_per_week: 5,
                  theoretical_classes: 5,
                  practical_classes: 0,
                  userEdited: false,
                },
              ]
              : cls.selected_subjects.filter(
                (s) => s.subject_id !== subject.subject_id
              ),
          }
          : cls
      )
    );
    // if (runClassTour && classStepIndex === 13 && isChecked) {
    //   setTimeout(() => {
    //     const periodsEl = document.querySelector(".add-class-periods-step");

    //     if (periodsEl) {
    //       periodsEl.scrollIntoView({ behavior: "smooth", block: "center" });
    //     }

    //     window.dispatchEvent(new Event("resize")); // Recalculate spotlight
    //     setClassStepIndex(14); // Move to "Periods" step
    //   }, 300);
    // }
  };

  // Period entry
  const handlePeriodChange = (classId, subjectId, field, value) => {
    setNewClasses((prevClasses) =>
      prevClasses.map((cls) =>
        cls.id === classId
          ? {
            ...cls,
            selected_subjects: cls.selected_subjects.map((sub) =>
              sub.subject_id === subjectId
                ? {
                  ...sub,
                  [field]: Number(value) || 0,
                  userEdited: true, // ⭐ Very important for Step 5 → Step 6
                }
                : sub
            ),
          }
          : cls
      )
    );
  };

  // Submit
  const handleSubmit = (e) => {
    e.preventDefault();

    for (const cls of newClasses) {
      if (!cls.class_name.trim()) {
        alert("Class name is required");
        return;
      }
    }

    const processed = newClasses.map((cls) => {
      const labels = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];
      return {
        ...cls,
        sectionNames: Array.from(
          { length: cls.numberOfSections },
          (_, i) => `${cls.class_name} ${labels[i]}`
        ),
      };
    });

    handleNewClassSubmit(processed);
    setNewClasses([createNewClassTemplate()]);

    setTimeout(() => {
      navigate("/timetable/sections", {
        state: { startSectionTour: true },  // 👈 flag for sections tour
      });
    }, 300);
  };

  const generateSectionNames = (name, count) => {
    const labels = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];
    return Array.from({ length: count }, (_, i) => `${name} ${labels[i]}`);
  };
  <style>
    {`
  .add-class-scroll-container {
    max-height: 75vh;
    overflow-y: auto;
    padding-right: 10px;
  }
`}
  </style>


  return (
    <Form onSubmit={handleSubmit}>
      <Card>
        <Card.Header>
          <h5 className="mb-0 d-flex align-items-center">
            <PlusCircle size={24} className="me-2" />
            Add New Class(es)
          </h5>
        </Card.Header>

        <Card.Body>
          <div ref={scrollRef} className="add-class-scroll-container">
            {newClasses.map((cls, index) => (
              <Card key={cls.id} className="mb-4 shadow-sm">
                <Card.Header className="d-flex justify-content-between bg-light">
                  <span className="fw-bold">New Class #{index + 1}</span>

                  {newClasses.length > 1 && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() =>
                        setNewClasses((prev) =>
                          prev.filter((c) => c.id !== cls.id)
                        )
                      }
                    >
                      <Trash2 size={16} className="me-1" /> Remove
                    </Button>
                  )}
                </Card.Header>

                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Class Name</Form.Label>
                        <Form.Control
                          type="text"
                          className="add-class-name-step"
                          placeholder="e.g., Grade 1"
                          value={cls.class_name}
                          onChange={(e) => {
                            const val = e.target.value;
                            handleClassChange(cls.id, "class_name", val);
                            dispatch(setFormValue({
                              key: "className",
                              value: val,
                            }));
                          }}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Number of Sections</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="10"
                          value={cls.numberOfSections}
                          onChange={(e) =>
                            handleClassChange(
                              cls.id,
                              "numberOfSections",
                              parseInt(e.target.value) || 1
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* CLASS TEACHERS */}
                  <div className="mb-3 p-3 border rounded">
                    <h6>Assign Class Teachers</h6>
                    {Array.from({ length: cls.numberOfSections }).map(
                      (_, secIndex) => (
                        <InputGroup key={secIndex} className="mb-2">
                          <InputGroup.Text style={{ width: "120px" }}>
                            {generateSectionNames(
                              cls.class_name || "Class",
                              cls.numberOfSections
                            )[secIndex]?.split(" ").pop()}
                          </InputGroup.Text>

                          <Form.Select
                            className={secIndex === 0 ? "add-class-teacher-step" : ""}
                            value={cls.sectionTeachers[`section-${secIndex}`] || ""}
                            onChange={(e) => {
                              handleTeacherChange(
                                cls.id,
                                secIndex,
                                e.target.value
                              );
                              dispatch(setStepValid(true));   // 🔓 unlock Next
                            }}

                          >
                            <option value="">Select a teacher...</option>
                            {teachers?.map((t) => (
                              <option key={t.teacher_id} value={t.teacher_id}>
                                {t.name}
                              </option>
                            ))}
                          </Form.Select>
                        </InputGroup>
                      )
                    )}
                  </div>

                  {/* SUBJECTS */}
                  <div className="mb-3 p-3 border rounded">
                    <h6>Select Subjects & Weekly Periods</h6>

                    {subjects?.length > 0 ? (
                      subjects.map((subject, i) => {
                        const current = cls.selected_subjects.find(
                          (s) => s.subject_id === subject.subject_id
                        );

                        return (
                          <div
                            key={subject.subject_id}
                            className="mb-3 pb-2 border-bottom add-class-subject-step"
                          >
                            <Form.Check
                              type="checkbox"

                              label={<strong>{subject.subject_name}</strong>}
                              checked={!!current}
                              onChange={(e) => {
                                const checked = e.target.checked;

                                handleSubjectToggle(
                                  cls.id,
                                  subject,
                                  checked
                                );

                                // 🚀 Auto move joyride to next step when checked
                                if (checked && run) {   // run = globalJoyride.run
                                  setTimeout(() => {
                                    dispatch(setStepIndex(15));   // 👈 NEXT STEP
                                    window.dispatchEvent(new Event("resize"));
                                  }, 200);
                                }
                              }}

                            />

                            {current && (
                              <Row
                                className={`mt-2 ps-4 add-class-periods-step`}
                              >
                                {["max_periods_per_week", "theoretical_classes", "practical_classes"].map(
                                  (field, idx) => (
                                    <Col sm={4} key={idx}>
                                      <Form.Group>
                                        <Form.Label>
                                          {field === "max_periods_per_week"
                                            ? "Periods/Week"
                                            : field === "theoretical_classes"
                                              ? "Theory"
                                              : "Practical"}
                                        </Form.Label>

                                        <Form.Control
                                          type="number"
                                          value={current[field]}
                                          onChange={(e) => {
                                            handlePeriodChange(
                                              cls.id,
                                              subject.subject_id,
                                              field,
                                              e.target.value
                                            );
                                            dispatch(setStepValid(true)); 
                                          }}

                                        />
                                      </Form.Group>
                                    </Col>
                                  )
                                )}
                              </Row>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p>No subjects found.</p>
                    )}
                  </div>
                </Card.Body>
              </Card>
            ))}

            {/* SUBMIT BUTTON */}
            <div className="d-flex justify-content-end">
              <Button
                type="submit"
                className="add-class-submit-step"
                disabled={loading}
              >
                Submit {newClasses.length} Class(es)
              </Button>
            </div>
          </div>

        </Card.Body>
      </Card>
    </Form>

  );
};

export default AddClassTab;